import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, PencilLine, X } from 'lucide-react';
import type { GrammarExercise } from '@org/grammar-content';

// -- Feminist color palette (local copy — same convention as journey.tsx / mural.tsx) --
const P = {
  maxRed: '#D41920',
  charm: '#F2A0C4',
  white: '#FFFFFF',
  blush: '#FDF0ED',
  roseMist: '#F9E4E0',
  darkText: '#2D1A1F',
  midText: '#6B4F55',
  lightText: '#8A6F69',
  lightBorder: '#F0DAD5',
  green: '#1E7A46',
  greenSoft: '#E4F2E9',
};

const oswald = "'Oswald', sans-serif";

const bodyStyle: CSSProperties = {
  color: P.midText,
  fontSize: 15.5,
  lineHeight: 1.65,
  margin: 0,
};

const exerciseCardStyle: CSSProperties = {
  background: P.white,
  border: `1px solid ${P.lightBorder}`,
  borderRadius: 8,
  padding: '16px 16px 14px',
};

const smallButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: oswald,
  fontWeight: 600,
  fontSize: 12,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  padding: '8px 14px',
  borderRadius: 4,
  border: `2px solid ${P.maxRed}`,
  background: P.maxRed,
  color: P.white,
  cursor: 'pointer',
  lineHeight: 1.2,
};

function Feedback({ status, explanation }: { status: 'right' | 'wrong'; explanation?: string }) {
  const { t } = useTranslation('p2e');
  if (status === 'wrong') {
    return (
      <p style={{ ...bodyStyle, color: P.maxRed, fontWeight: 600, marginTop: 8, fontSize: 14 }}>
        {t('grammarAid.practice.tryAgain')}
      </p>
    );
  }
  return (
    <div style={{ background: P.greenSoft, borderRadius: 6, padding: '10px 12px', marginTop: 10 }}>
      <p style={{ ...bodyStyle, color: P.green, fontWeight: 700, fontSize: 14 }}>
        {t('grammarAid.practice.correct')}
      </p>
      {explanation ? <p style={{ ...bodyStyle, fontSize: 14, marginTop: 4 }}>{explanation}</p> : null}
    </div>
  );
}

function MultipleChoice({
  exercise,
  onSolved,
}: {
  exercise: Extract<GrammarExercise, { kind: 'multiple-choice' }>;
  onSolved: () => void;
}) {
  const [picked, setPicked] = useState<number[]>([]);
  const solved = picked.includes(exercise.correctIndex);
  const lastWrong = !solved && picked.length > 0;

  const pick = (index: number) => {
    if (solved || picked.includes(index)) return;
    setPicked((previous) => [...previous, index]);
    if (index === exercise.correctIndex) onSolved();
  };

  return (
    <div>
      <p style={{ ...bodyStyle, color: P.darkText }}>{exercise.prompt}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
        {exercise.options.map((option, index) => {
          const isCorrectPick = solved && index === exercise.correctIndex;
          const isWrongPick = picked.includes(index) && index !== exercise.correctIndex;
          return (
            <button
              key={option}
              type="button"
              onClick={() => pick(index)}
              disabled={solved || isWrongPick}
              style={{
                fontFamily: 'inherit',
                fontSize: 14.5,
                padding: '8px 14px',
                borderRadius: 6,
                cursor: solved || isWrongPick ? 'default' : 'pointer',
                border: `2px solid ${isCorrectPick ? P.green : isWrongPick ? P.maxRed : P.lightBorder}`,
                background: isCorrectPick ? P.greenSoft : isWrongPick ? P.roseMist : P.white,
                color: isCorrectPick ? P.green : isWrongPick ? P.maxRed : P.darkText,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {option}
              {isCorrectPick ? <Check size={14} aria-hidden /> : null}
              {isWrongPick ? <X size={14} aria-hidden /> : null}
            </button>
          );
        })}
      </div>
      {solved ? <Feedback status="right" explanation={exercise.explanation} /> : null}
      {lastWrong ? <Feedback status="wrong" /> : null}
    </div>
  );
}

function FillBlank({
  exercise,
  onSolved,
}: {
  exercise: Extract<GrammarExercise, { kind: 'fill-blank' }>;
  onSolved: () => void;
}) {
  const { t } = useTranslation('p2e');
  const [value, setValue] = useState('');
  const [status, setStatus] = useState<'idle' | 'wrong' | 'right'>('idle');
  const [attempts, setAttempts] = useState(0);
  const [revealed, setRevealed] = useState(false);

  const normalized = (text: string) => text.trim().toLowerCase().replace(/’/g, "'");
  const check = () => {
    if (status === 'right') return;
    const isRight = exercise.answers.some((answer) => normalized(answer) === normalized(value));
    setAttempts((previous) => previous + 1);
    if (isRight) {
      setStatus('right');
      onSolved();
    } else {
      setStatus('wrong');
    }
  };

  return (
    <div>
      <p style={{ ...bodyStyle, color: P.darkText }}>{exercise.prompt}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <input
          type="text"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            if (status === 'wrong') setStatus('idle');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') check();
          }}
          placeholder={t('grammarAid.practice.typeAnswer')}
          disabled={status === 'right'}
          style={{
            fontFamily: 'inherit',
            fontSize: 14.5,
            padding: '8px 12px',
            borderRadius: 6,
            border: `2px solid ${status === 'right' ? P.green : status === 'wrong' ? P.maxRed : P.lightBorder}`,
            background: status === 'right' ? P.greenSoft : P.white,
            color: P.darkText,
            minWidth: 180,
          }}
        />
        {status !== 'right' ? (
          <button type="button" onClick={check} style={smallButtonStyle}>
            {t('grammarAid.practice.check')}
          </button>
        ) : null}
        {status !== 'right' && attempts >= 2 && !revealed ? (
          <button
            type="button"
            onClick={() => setRevealed(true)}
            style={{ ...smallButtonStyle, background: 'transparent', color: P.maxRed }}
          >
            {t('grammarAid.practice.showAnswer')}
          </button>
        ) : null}
      </div>
      {revealed && status !== 'right' ? (
        <p style={{ ...bodyStyle, fontSize: 14, marginTop: 8 }}>
          {t('grammarAid.practice.answerWas', { answer: exercise.answers[0] })}
        </p>
      ) : null}
      {status === 'right' ? <Feedback status="right" explanation={exercise.explanation} /> : null}
      {status === 'wrong' ? <Feedback status="wrong" /> : null}
    </div>
  );
}

function Matching({
  exercise,
  onSolved,
}: {
  exercise: Extract<GrammarExercise, { kind: 'matching' }>;
  onSolved: () => void;
}) {
  const { t } = useTranslation('p2e');
  // Stable shuffle of the right-hand options for this mount.
  const shuffledRights = useMemo(
    () =>
      exercise.pairs
        .map((pair) => pair.right)
        .map((right) => ({ right, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map((entry) => entry.right),
    [exercise],
  );
  const [choices, setChoices] = useState<string[]>(exercise.pairs.map(() => ''));
  const [checked, setChecked] = useState(false);
  const solved = checked && exercise.pairs.every((pair, index) => choices[index] === pair.right);

  const check = () => {
    setChecked(true);
    if (exercise.pairs.every((pair, index) => choices[index] === pair.right)) onSolved();
  };

  return (
    <div>
      <p style={{ ...bodyStyle, color: P.darkText }}>{exercise.instruction}</p>
      <div style={{ display: 'grid', gap: 8, marginTop: 10, maxWidth: 360 }}>
        {exercise.pairs.map((pair, index) => {
          const rowRight = checked && choices[index] === pair.right;
          const rowWrong = checked && choices[index] !== '' && choices[index] !== pair.right;
          return (
            <div key={pair.left} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ ...bodyStyle, color: P.darkText, fontWeight: 600, minWidth: 90 }}>{pair.left}</span>
              <select
                value={choices[index]}
                onChange={(event) => {
                  const next = [...choices];
                  next[index] = event.target.value;
                  setChoices(next);
                  setChecked(false);
                }}
                disabled={solved}
                style={{
                  fontFamily: 'inherit',
                  fontSize: 14.5,
                  padding: '7px 10px',
                  borderRadius: 6,
                  border: `2px solid ${rowRight ? P.green : rowWrong ? P.maxRed : P.lightBorder}`,
                  background: rowRight ? P.greenSoft : P.white,
                  color: P.darkText,
                  flex: 1,
                }}
              >
                <option value="">—</option>
                {shuffledRights.map((right) => (
                  <option key={right} value={right}>
                    {right}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
      {!solved ? (
        <button
          type="button"
          onClick={check}
          disabled={choices.some((choice) => choice === '')}
          style={{ ...smallButtonStyle, marginTop: 10, opacity: choices.some((c) => c === '') ? 0.5 : 1 }}
        >
          {t('grammarAid.practice.check')}
        </button>
      ) : null}
      {solved ? <Feedback status="right" explanation={exercise.explanation} /> : null}
      {checked && !solved ? <Feedback status="wrong" /> : null}
    </div>
  );
}

export function GrammarPractice({ exercises }: { exercises: GrammarExercise[] }) {
  const { t } = useTranslation('p2e');
  const [solvedCount, setSolvedCount] = useState(0);
  const allSolved = solvedCount >= exercises.length;
  const markSolved = () => setSolvedCount((previous) => previous + 1);

  return (
    <section
      aria-label={t('grammarAid.practiceTitle')}
      style={{
        marginTop: 20,
        background: P.blush,
        border: `1px solid ${P.charm}`,
        borderRadius: 8,
        padding: '18px 18px 16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 34,
            height: 34,
            borderRadius: 6,
            background: P.maxRed,
            color: P.white,
          }}
        >
          <PencilLine size={16} aria-hidden />
        </span>
        <h2
          style={{
            fontFamily: oswald,
            fontWeight: 600,
            fontSize: 14,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: P.darkText,
            margin: 0,
          }}
        >
          {t('grammarAid.practiceTitle')}
        </h2>
        <span style={{ fontSize: 12, color: P.lightText, marginLeft: 'auto' }}>
          {solvedCount}/{exercises.length}
        </span>
      </div>
      <div style={{ display: 'grid', gap: 12 }}>
        {exercises.map((exercise, index) => (
          <div key={index} style={exerciseCardStyle}>
            <p
              style={{
                fontFamily: oswald,
                fontWeight: 600,
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: P.maxRed,
                margin: '0 0 8px',
              }}
            >
              {t('grammarAid.practice.exerciseLabel', { number: index + 1 })}
            </p>
            {exercise.kind === 'multiple-choice' ? (
              <MultipleChoice exercise={exercise} onSolved={markSolved} />
            ) : exercise.kind === 'fill-blank' ? (
              <FillBlank exercise={exercise} onSolved={markSolved} />
            ) : (
              <Matching exercise={exercise} onSolved={markSolved} />
            )}
          </div>
        ))}
      </div>
      {allSolved ? (
        <p
          style={{
            fontFamily: oswald,
            fontWeight: 600,
            fontSize: 13,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: P.green,
            textAlign: 'center',
            margin: '14px 0 0',
          }}
        >
          {t('grammarAid.practice.allDone')}
        </p>
      ) : null}
    </section>
  );
}
